import React, { ReactElement } from 'react';
import cn from 'classnames';

import { SelectedSort } from 'Shared/constants/types';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './ActionItemsSortingHeader.scss';
import WpIcon from 'Wpds/components/icon/WpIcon';

type Props = {
  selectedSort: SelectedSort;
  reorderHeaders?: boolean;
  inUppercase?: boolean;
  userPreview?: ReactElement;
  withWorkspaceSection?: boolean;
  withOwnerSection?: boolean;
  withDueDateSection?: boolean;
  onClickSortOption?: (event: React.MouseEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
  showTableHeaders?: boolean;
};

function renderSortIcon(sort) {
  if (sort === 'default') return null;
  const name = sort === 'ascending' ? 'chevron-down' : 'chevron-up';
  return (
    <span className="ml-2">
      <WpIcon name={name} size="md" />
    </span>
  );
}

function ActionItemsSortingHeader({
  children,
  selectedSort: { actionItems, owner, dueDate, workspace },
  withWorkspaceSection = false,
  withOwnerSection = false,
  withDueDateSection = false,
  reorderHeaders = true,
  onClickSortOption,
  inUppercase = true,
  userPreview,
  showTableHeaders = true,
}: Props) {
  const { tr } = useTranslation();
  return (
    <>
      {showTableHeaders && (
        <div
          className={cn('action-items-sorting-header d-none d-sm-flex mb-1', {
            'action-items-sorting-header--uppercase': inUppercase,
          })}
        >
          <div
            className={cn('action-items-sorting-header-action d-flex', {
              'action-items-sorting-header-action--all-sections': withWorkspaceSection && withOwnerSection,
              'text-uppercase': inUppercase && !userPreview,
              'order-1': reorderHeaders,
            })}
            id="actionItems"
            onClick={onClickSortOption}
          >
            {userPreview || <span className="cursor-pointer">{tr('perform.action_items_header.action_items')}</span>}
            {renderSortIcon(actionItems)}
          </div>
          {withOwnerSection && (
            <div
              className={cn('action-items-sorting-header-owner', { 'order-3': reorderHeaders })}
              id="owner"
              onClick={onClickSortOption}
            >
              <span className="cursor-pointer">{tr('perform.action_items_header.owner')}</span>
              {renderSortIcon(owner)}
            </div>
          )}
          {withDueDateSection && (
            <div
              className={cn('action-items-sorting-header-due-date', { 'order-2': reorderHeaders })}
              id="dueDate"
              onClick={onClickSortOption}
            >
              <span className="cursor-pointer">{tr('perform.action_items_header.due_date')}</span>
              {renderSortIcon(dueDate)}
            </div>
          )}
          {withWorkspaceSection && (
            <div
              className={cn('action-items-sorting-header-workspace', { 'order-4': reorderHeaders })}
              id="workspace"
              onClick={onClickSortOption}
            >
              <span className="cursor-pointer">{tr('perform.action_items_header.workspace')}</span>
              {renderSortIcon(workspace)}
            </div>
          )}
        </div>
      )}
      {children}
    </>
  );
}

export default ActionItemsSortingHeader;
